import React, { FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { palette, SimpleLoader, WorldMap } from '@dev/ee-components';
import styled from 'styled-components';
import { map } from 'lodash-es';
import { CampaignType, LocationDetails, ReportLocationStats } from '@dev/ee-api/ee-api-models';

import { getReportsState } from '@/store/reports/actions';
import { GetLocationStatsByCountryIso, getCampaignsStore } from '@/store/campaigns/actions';
import { AbSplitDisplayMode } from '@/store/campaigns/types';
import { GetReportsLocationStatsByName } from '@/store/reports/actions';
import { GeographyWorldMapProps, mapRowsParams, WorldMapsEnum } from '../CampaignReportGeographyInterface';
import { getIsWinner } from '../../../helperFunctions';

type CountryDataType = ReportLocationStats & LocationDetails;

const LoaderWrapper = styled.div`
    height: 515px;
    width: 100%;
    display: flex;
    div:first-child {
        margin: auto;
    }
`;
const MapWrapper = styled.div`
    position: relative;
    display: flex;
    width: 99%;
    height: 515px;
    overflow: hidden;
`;

const isWorldMapSelected = (selectedMap): boolean => selectedMap === WorldMapsEnum.World;

const worldMapsDataSet = (
    locationStats,
    locationCountryStats,
    selectedMap,
    opensoccured,
    clicksoccured
): {
    countryId: string;
    name: string;
    multipleValues: mapRowsParams[];
}[] => {
    const countriesList = isWorldMapSelected(selectedMap)
        ? locationStats?.dataSet
        : locationCountryStats?.dataSetByCountry;

    return map(
        countriesList,
        (
            CountryData: CountryDataType
        ): {
            countryId: string;
            name: string;
            multipleValues: mapRowsParams[];
        } => ({
            countryId: isWorldMapSelected(selectedMap)
                ? CountryData.CountryISO.toLowerCase()
                : (CountryData.State as string),
            name: isWorldMapSelected(selectedMap) ? CountryData.Country : CountryData.State,
            multipleValues: [
                { value: CountryData.OpenCount, description: opensoccured },
                { value: CountryData.ClickCount, description: clicksoccured },
            ],
        })
    );
};

const GeographyWorldMap: FC<GeographyWorldMapProps> = (props: GeographyWorldMapProps): JSX.Element => {
    const { locationStats } = useSelector(getReportsState);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { locationCountryStats, pickedCampaign, abSplitHeaderControls } = useSelector(getCampaignsStore);
    const { selectedMap, setSelectedMap, setPage } = props;
    const params = useParams<{
        type: CampaignType;
    }>();

    const statsLoading = locationStats?.loading || locationCountryStats?.loading;

    const handleRegionClick = (countryId: string): void => {
        const { currentTemplate, displayMode } = abSplitHeaderControls;
        const isWinnerDisplayMode = displayMode === AbSplitDisplayMode.Winner;

        if (params.type === CampaignType.AbSplit && !isWinnerDisplayMode) {
            dispatch(GetLocationStatsByCountryIso({ countryiso: countryId, name: currentTemplate }));
        } else if (params.type === CampaignType.AbSplit && isWinnerDisplayMode) {
            const winnerChannel = getIsWinner(pickedCampaign?.simpleStats?.CampaignTemplatesSummary);
            dispatch(GetLocationStatsByCountryIso({ countryiso: countryId, name: winnerChannel.Name }));
        } else {
            dispatch(GetLocationStatsByCountryIso({ countryiso: countryId, name: pickedCampaign.campaign.Name }));
        }

        setSelectedMap(countryId as WorldMapsEnum);
        setPage(1);
    };

    const handleReturnToWorldMap = (): void => {
        setSelectedMap(WorldMapsEnum.World);
        setPage(1);

        const { currentTemplate, displayMode } = abSplitHeaderControls;
        const isWinnerDisplayMode = displayMode === AbSplitDisplayMode.Winner;

        if (params.type === CampaignType.AbSplit && !isWinnerDisplayMode) {
            dispatch(GetReportsLocationStatsByName({ channelName: currentTemplate }));
        } else if (params.type === CampaignType.AbSplit && isWinnerDisplayMode) {
            const winnerChannel = getIsWinner(pickedCampaign?.simpleStats?.CampaignTemplatesSummary);
            dispatch(GetReportsLocationStatsByName({ channelName: winnerChannel.Name }));
        } else {
            dispatch(GetReportsLocationStatsByName({ channelName: pickedCampaign.campaign.Name }));
        }
    };

    return statsLoading ? (
        <LoaderWrapper>
            <SimpleLoader color={palette.indigo[6]} />
        </LoaderWrapper>
    ) : (
        <MapWrapper>
            <WorldMap
                getCountryMap={selectedMap}
                dataSet={worldMapsDataSet(
                    locationStats,
                    locationCountryStats,
                    selectedMap,
                    t('opensoccured'),
                    t('clicksoccured')
                )}
                hoverColor={palette.gray[9]}
                enableGradient
                onRegionClick={(countryId) => {
                    handleRegionClick(countryId);
                }}
                onReturnToWorldMapClick={handleReturnToWorldMap}
                showCenterMapButton
            />
        </MapWrapper>
    );
};

export default GeographyWorldMap;
